// Summer 2026 bracket structure (Official 48-team format)
// 48 teams → 12 groups of 4 → Top 2 + 8 best 3rd = 32 in knockout
// Round of 32 → Round of 16 → QF → SF → Final

export interface Team {
  name: string;
  code: string;
  group: string;
  seed: number; // 1=winner, 2=runner-up, 3=best 3rd
}

export interface BracketSlot {
  id: string;
  round: number; // 0=R32, 1=R16, 2=QF, 3=SF, 4=Final
  position: number;
  teamA?: Team;
  teamB?: Team;
  feedsInto?: string;
  feedsPosition?: "A" | "B";
}

export const ROUNDS = [
  "Round of 32",
  "Round of 16",
  "Quarterfinals",
  "Semifinals",
  "Final",
  "Champion",
];

// Pre-seeded teams by group (using expected qualifiers)
export const groupTeams: Record<string, Team[]> = {
  A: [
    { name: "Mexico", code: "MEX", group: "A", seed: 1 },
    { name: "South Africa", code: "ZAF", group: "A", seed: 2 },
    { name: "South Korea", code: "KOR", group: "A", seed: 3 },
    { name: "Czechia", code: "CZE", group: "A", seed: 4 },
  ],
  B: [
    { name: "Canada", code: "CAN", group: "B", seed: 1 },
    { name: "Switzerland", code: "SUI", group: "B", seed: 2 },
    { name: "Qatar", code: "QAT", group: "B", seed: 3 },
    { name: "Bosnia and Herzegovina", code: "BIH", group: "B", seed: 4 },
  ],
  C: [
    { name: "Brazil", code: "BRA", group: "C", seed: 1 },
    { name: "Morocco", code: "MAR", group: "C", seed: 2 },
    { name: "Haiti", code: "HTI", group: "C", seed: 3 },
    { name: "Scotland", code: "SCO", group: "C", seed: 4 },
  ],
  D: [
    { name: "United States", code: "USA", group: "D", seed: 1 },
    { name: "Paraguay", code: "PAR", group: "D", seed: 2 },
    { name: "Australia", code: "AUS", group: "D", seed: 3 },
    { name: "Türkiye", code: "TUR", group: "D", seed: 4 },
  ],
  E: [
    { name: "Germany", code: "GER", group: "E", seed: 1 },
    { name: "Ecuador", code: "ECU", group: "E", seed: 2 },
    { name: "Ivory Coast", code: "CIV", group: "E", seed: 3 },
    { name: "Curaçao", code: "CUW", group: "E", seed: 4 },
  ],
  F: [
    { name: "Netherlands", code: "NED", group: "F", seed: 1 },
    { name: "Japan", code: "JPN", group: "F", seed: 2 },
    { name: "Tunisia", code: "TUN", group: "F", seed: 3 },
    { name: "Sweden", code: "SWE", group: "F", seed: 4 },
  ],
  G: [
    { name: "Belgium", code: "BEL", group: "G", seed: 1 },
    { name: "Egypt", code: "EGY", group: "G", seed: 2 },
    { name: "Iran", code: "IRN", group: "G", seed: 3 },
    { name: "New Zealand", code: "NZL", group: "G", seed: 4 },
  ],
  H: [
    { name: "Spain", code: "ESP", group: "H", seed: 1 },
    { name: "Uruguay", code: "URU", group: "H", seed: 2 },
    { name: "Saudi Arabia", code: "KSA", group: "H", seed: 3 },
    { name: "Cape Verde", code: "CPV", group: "H", seed: 4 },
  ],
  I: [
    { name: "France", code: "FRA", group: "I", seed: 1 },
    { name: "Senegal", code: "SEN", group: "I", seed: 2 },
    { name: "Norway", code: "NOR", group: "I", seed: 3 },
    { name: "Iraq", code: "IRQ", group: "I", seed: 4 },
  ],
  J: [
    { name: "Argentina", code: "ARG", group: "J", seed: 1 },
    { name: "Austria", code: "AUT", group: "J", seed: 2 },
    { name: "Algeria", code: "ALG", group: "J", seed: 3 },
    { name: "Jordan", code: "JOR", group: "J", seed: 4 },
  ],
  K: [
    { name: "Portugal", code: "POR", group: "K", seed: 1 },
    { name: "Colombia", code: "COL", group: "K", seed: 2 },
    { name: "Uzbekistan", code: "UZB", group: "K", seed: 3 },
    { name: "DR Congo", code: "COD", group: "K", seed: 4 },
  ],
  L: [
    { name: "England", code: "ENG", group: "L", seed: 1 },
    { name: "Croatia", code: "CRO", group: "L", seed: 2 },
    { name: "Ghana", code: "GHA", group: "L", seed: 3 },
    { name: "Panama", code: "PAN", group: "L", seed: 4 },
  ],
};

export const flagEmoji: Record<string, string> = {
  MEX: "🇲🇽", ZAF: "🇿🇦", KOR: "🇰🇷", CAN: "🇨🇦", SUI: "🇨🇭", QAT: "🇶🇦",
  BRA: "🇧🇷", MAR: "🇲🇦", HTI: "🇭🇹", SCO: "🏴󠁧󠁢󠁳󠁣󠁴󠁿", USA: "🇺🇸", PAR: "🇵🇾",
  AUS: "🇦🇺", GER: "🇩🇪", ECU: "🇪🇨", CIV: "🇨🇮", CUW: "🇨🇼", NED: "🇳🇱",
  JPN: "🇯🇵", TUN: "🇹🇳", BEL: "🇧🇪", EGY: "🇪🇬", IRN: "🇮🇷", NZL: "🇳🇿",
  ESP: "🇪🇸", URU: "🇺🇾", KSA: "🇸🇦", CPV: "🇨🇻", FRA: "🇫🇷", SEN: "🇸🇳",
  NOR: "🇳🇴", ARG: "🇦🇷", AUT: "🇦🇹", ALG: "🇩🇿", JOR: "🇯🇴", POR: "🇵🇹",
  COL: "🇨🇴", UZB: "🇺🇿", ENG: "🏴󠁧󠁢󠁥󠁮󠁧󠁿", CRO: "🇭🇷", GHA: "🇬🇭", PAN: "🇵🇦",
  BIH: "🇧🇦", TUR: "🇹🇷", SWE: "🇸🇪", IRQ: "🇮🇶", COD: "🇨🇩", CZE: "🇨🇿",
};

// ── Official R32 matchup structure ──
// Slot types: fixed (group winner/runner-up) or third-place (resolved dynamically)
export interface R32Slot {
  group: string;
  position: "1st" | "2nd" | "3rd";
}

export interface R32ThirdSlot {
  thirdPlaceFor: string; // group letter of the winner they face (A/B/D/E/G/I/K/L)
}

export interface R32Match {
  id: string;
  label: string;
  slotA: R32Slot;
  slotB: R32Slot | R32ThirdSlot;
}

export function isThirdPlaceSlot(slot: R32Slot | R32ThirdSlot): slot is R32ThirdSlot {
  return "thirdPlaceFor" in slot;
}

// The 8 group winners that face 3rd-place teams, in combo-table column order
export const WINNERS_FACING_THIRDS = ["A", "B", "D", "E", "G", "I", "K", "L"] as const;

// R32 matches ordered for bracket visual layout:
// Left side (0-7): feeds into left bracket half
// Right side (8-15): feeds into right bracket half
// Adjacent pairs [0,1], [2,3], etc. feed into the same R16 match
export const r32Matches: R32Match[] = [
  // ── Left bracket half ──
  // R16-1: W(M74) vs W(M77)
  { id: "r32-1",  label: "M74", slotA: { group: "E", position: "1st" }, slotB: { thirdPlaceFor: "E" } },
  { id: "r32-2",  label: "M77", slotA: { group: "I", position: "1st" }, slotB: { thirdPlaceFor: "I" } },
  // R16-2: W(M73) vs W(M75)
  { id: "r32-3",  label: "M73", slotA: { group: "A", position: "2nd" }, slotB: { group: "B", position: "2nd" } },
  { id: "r32-4",  label: "M75", slotA: { group: "F", position: "1st" }, slotB: { group: "C", position: "2nd" } },
  // R16-3: W(M83) vs W(M84)
  { id: "r32-5",  label: "M83", slotA: { group: "K", position: "2nd" }, slotB: { group: "L", position: "2nd" } },
  { id: "r32-6",  label: "M84", slotA: { group: "H", position: "1st" }, slotB: { group: "J", position: "2nd" } },
  // R16-4: W(M81) vs W(M82)
  { id: "r32-7",  label: "M81", slotA: { group: "D", position: "1st" }, slotB: { thirdPlaceFor: "D" } },
  { id: "r32-8",  label: "M82", slotA: { group: "G", position: "1st" }, slotB: { thirdPlaceFor: "G" } },

  // ── Right bracket half ──
  // R16-5: W(M76) vs W(M78)
  { id: "r32-9",  label: "M76", slotA: { group: "C", position: "1st" }, slotB: { group: "F", position: "2nd" } },
  { id: "r32-10", label: "M78", slotA: { group: "E", position: "2nd" }, slotB: { group: "I", position: "2nd" } },
  // R16-6: W(M79) vs W(M80)
  { id: "r32-11", label: "M79", slotA: { group: "A", position: "1st" }, slotB: { thirdPlaceFor: "A" } },
  { id: "r32-12", label: "M80", slotA: { group: "L", position: "1st" }, slotB: { thirdPlaceFor: "L" } },
  // R16-7: W(M86) vs W(M88)
  { id: "r32-13", label: "M86", slotA: { group: "J", position: "1st" }, slotB: { group: "H", position: "2nd" } },
  { id: "r32-14", label: "M88", slotA: { group: "D", position: "2nd" }, slotB: { group: "G", position: "2nd" } },
  // R16-8: W(M85) vs W(M87)
  { id: "r32-15", label: "M85", slotA: { group: "B", position: "1st" }, slotB: { thirdPlaceFor: "B" } },
  { id: "r32-16", label: "M87", slotA: { group: "K", position: "1st" }, slotB: { thirdPlaceFor: "K" } },
];

// Resolve which 3rd-place team a specific winner faces, given the advancing thirds
import { THIRD_PLACE_COMBOS } from "@/data/thirdPlaceCombos";

export function resolveThirdPlaceAssignment(
  advancingThirds: string[], // sorted array of 8 group letters
  winnerGroup: string, // A, B, D, E, G, I, K, L
): string | undefined {
  if (advancingThirds.length !== 8) return undefined;
  const key = [...advancingThirds].sort().join("");
  const assignment = THIRD_PLACE_COMBOS[key];
  if (!assignment) return undefined;
  const idx = WINNERS_FACING_THIRDS.indexOf(winnerGroup as any);
  if (idx === -1) return undefined;
  return assignment[idx]; // returns the group letter of the 3rd-place team
}

// All teams available for picking (flattened from groups)
export function getAllTeams(): Team[] {
  return Object.values(groupTeams).flat();
}
